import React from 'react';
import styled, { DefaultTheme } from 'styled-components';
import { space, layout } from 'styled-system';

import { TypographyStyle } from 'src/packages/core/text';
import { MarginButton } from 'src/packages/core';
import { hexToRGBA } from 'src/utils/hex-to-rgba';

import {
  ButtonProps,
  ButtonPadding,
  ButtonSize,
  ButtonStyles,
  ButtonVariant,
  IconSize,
} from './Button.types';

/*const rtlCSS = css`
  direction: rtl;
  unicode-bidi: bidi-override;
`;*/

// Add this  in StyledButton > span (line 205)
/*${(props) => (props.theme.rtl ? rtlCSS : null)};*/

export const getStyles = (variant: ButtonVariant, themeObject: DefaultTheme): ButtonStyles => {
  const getTypographyStyles = (): TypographyStyle => {
    let fontSize;
    let lineHeight;
    const fontWeight = 500;
    const fontFamily = 'Inter-Medium';

    if (variant.indexOf('Large') > -1) {
      fontSize = '20px';
      lineHeight = '24px';
    } else if (variant.indexOf('Default') > -1) {
      fontSize = '16px';
      lineHeight = '26px';
    } else {
      fontSize = '14px';
      lineHeight = '22px';
    }

    return {
      fontSize,
      lineHeight,
      fontWeight,
      fontFamily,
    };
  };

  const getBrandStyles = (
    padding: string,
    height: ButtonSize,
    iconSize: IconSize
  ): ButtonStyles => {
    return {
      backgroundColor: themeObject.colors.palette.brand,
      backgroundColorHover: themeObject.colors.palette.brandHover,
      backgroundColorDisabled: themeObject.colors.background.secondary.BG4,
      border: '0',
      borderHover: '0',
      borderDisabled: '0',
      color: themeObject.colors.palette.black,
      colorHover: themeObject.colors.palette.black,
      iconColor: themeObject.colors.palette.black,
      iconColorHover: themeObject.colors.palette.black,
      colorDisabled: hexToRGBA(themeObject.colors.text.primary, 0.2),
      padding,
      height,
      iconSize,
      ...getTypographyStyles(),
    };
  };

  const getSecondaryStyles = (
    padding: string,
    height: ButtonSize,
    iconSize: IconSize
  ): ButtonStyles => {
    const contentColor = themeObject.colors.text.inverse;
    const backgroundColor = themeObject.colors.background.inverse;
    const hoverBackgroundColor = themeObject.colors.background.secondary.BG1;
    return {
      backgroundColor: backgroundColor,
      backgroundColorHover: `linear-gradient(${hexToRGBA(hoverBackgroundColor, 0.1)}, ${hexToRGBA(
        hoverBackgroundColor,
        0.2
      )}), ${backgroundColor}`,
      backgroundColorDisabled: themeObject.colors.background.secondary.BG4,
      border: '0',
      borderHover: '0',
      borderDisabled: '0',
      color: contentColor,
      colorHover: contentColor,
      iconColor: contentColor,
      iconColorHover: contentColor,
      colorDisabled: hexToRGBA(themeObject.colors.text.primary, 0.2),
      padding,
      height,
      iconSize,
      ...getTypographyStyles(),
    };
  };

  const getPrimaryStyles = (
    borderThickness: number,
    padding: string,
    height: ButtonSize,
    iconSize: IconSize
  ): ButtonStyles => {
    return {
      backgroundColor: themeObject.colors.palette.primary,
      backgroundColorHover: `linear-gradient(${hexToRGBA(
        themeObject.colors.background.secondary.BG1,
        0.1
      )}, ${hexToRGBA(themeObject.colors.background.secondary.BG1, 0.2)}), ${
        themeObject.colors.palette.primary
      }`,
      backgroundColorDisabled: themeObject.colors.background.secondary.BG4,
      border: `${borderThickness}px solid ${themeObject.colors.palette.primary}`,
      borderHover: `${borderThickness}px solid ${themeObject.colors.palette.primaryHover}`,
      borderDisabled: `${borderThickness}px solid ${themeObject.colors.background.secondary.BG4}`,
      color: themeObject.colors.palette.black,
      colorHover: themeObject.colors.palette.black,
      iconColor: themeObject.colors.palette.black,
      iconColorHover: themeObject.colors.palette.black,
      colorDisabled: hexToRGBA(themeObject.colors.text.primary, 0.2),
      padding,
      height,
      iconSize,
      ...getTypographyStyles(),
    };
  };

  const getTertiaryStyles = (
    borderThickness: number,
    padding: string,
    height: ButtonSize,
    iconSize: IconSize
  ): ButtonStyles => {
    return {
      backgroundColor: `linear-gradient(${themeObject.colors.background.secondary.BG1} 0 0) padding-box, ${themeObject.colors.palette.brand} border-box`,
      backgroundColorHover: 'transparent',
      backgroundColorDisabled: themeObject.colors.background.secondary.BG4,
      border: `${borderThickness}px solid transparent`,
      borderHover: `${borderThickness}px solid ${themeObject.colors.text.primary}`,
      borderDisabled: `${borderThickness}px solid ${themeObject.colors.background.secondary.BG4}`,
      color: themeObject.colors.text.primary,
      colorHover: themeObject.colors.text.primary,
      iconColor: themeObject.colors.text.primary,
      iconColorHover: themeObject.colors.text.primary,
      colorDisabled: hexToRGBA(themeObject.colors.text.primary, 0.2),
      padding,
      height,
      iconSize,
      ...getTypographyStyles(),
    };
  };

  const getGhostStyles = (
    padding: string,
    height: ButtonSize,
    iconSize: IconSize
  ): ButtonStyles => {
    return {
      backgroundColor: 'transparent',
      backgroundColorHover: 'transparent',
      backgroundColorDisabled: 'transparent',
      border: '0',
      borderHover: '0',
      borderDisabled: '0',
      color: themeObject.colors.text.primary,
      colorHover: themeObject.colors.palette.primary,
      iconColor: themeObject.colors.text.primary,
      iconColorHover: themeObject.colors.palette.primary,
      colorDisabled: hexToRGBA(themeObject.colors.text.primary, 0.2),
      padding,
      height,
      iconSize,
      ...getTypographyStyles(),
    };
  };

  const styles = {
    primaryLarge: getPrimaryStyles(2, ButtonPadding.LARGE, ButtonSize.LARGE, IconSize.LARGE),
    primaryDefault: getPrimaryStyles(
      1,
      ButtonPadding.DEFAULT,
      ButtonSize.DEFAULT,
      IconSize.DEFAULT
    ),
    primaryMedium: getPrimaryStyles(1, ButtonPadding.MEDIUM, ButtonSize.MEDIUM, IconSize.MEDIUM),
    primarySmall: getPrimaryStyles(1, ButtonPadding.SMALL, ButtonSize.SMALL, IconSize.SMALL),
    secondaryLarge: getSecondaryStyles(ButtonPadding.LARGE, ButtonSize.LARGE, IconSize.LARGE),
    secondaryDefault: getSecondaryStyles(
      ButtonPadding.DEFAULT,
      ButtonSize.DEFAULT,
      IconSize.DEFAULT
    ),
    secondaryMedium: getSecondaryStyles(ButtonPadding.MEDIUM, ButtonSize.MEDIUM, IconSize.MEDIUM),
    secondarySmall: getSecondaryStyles(ButtonPadding.SMALL, ButtonSize.SMALL, IconSize.SMALL),
    brandLarge: getBrandStyles(ButtonPadding.LARGE, ButtonSize.LARGE, IconSize.LARGE),
    brandDefault: getBrandStyles(ButtonPadding.DEFAULT, ButtonSize.DEFAULT, IconSize.DEFAULT),
    brandMedium: getBrandStyles(ButtonPadding.MEDIUM, ButtonSize.MEDIUM, IconSize.MEDIUM),
    brandSmall: getBrandStyles(ButtonPadding.SMALL, ButtonSize.SMALL, IconSize.SMALL),
    ghostLarge: getGhostStyles(ButtonPadding.LARGE, ButtonSize.LARGE, IconSize.LARGE),
    ghostDefault: getGhostStyles(ButtonPadding.DEFAULT, ButtonSize.DEFAULT, IconSize.DEFAULT),
    ghostMedium: getGhostStyles(ButtonPadding.MEDIUM, ButtonSize.MEDIUM, IconSize.MEDIUM),
    ghostSmall: getGhostStyles(ButtonPadding.SMALL, ButtonSize.SMALL, IconSize.SMALL),
    tertiaryLarge: getTertiaryStyles(2, ButtonPadding.LARGE, ButtonSize.LARGE, IconSize.LARGE),
    tertiaryDefault: getTertiaryStyles(
      1,
      ButtonPadding.DEFAULT,
      ButtonSize.DEFAULT,
      IconSize.DEFAULT
    ),
    tertiaryMedium: getTertiaryStyles(1, ButtonPadding.MEDIUM, ButtonSize.MEDIUM, IconSize.MEDIUM),
    tertiarySmall: getTertiaryStyles(1, ButtonPadding.SMALL, ButtonSize.SMALL, IconSize.SMALL),
  };

  return styles[variant];
};

export const StyledButton = styled((props) => <MarginButton {...props} />)<
  ButtonProps & { styles: ButtonStyles }
>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: ${(props) => (props.theme.rtl ? 'row-reverse' : 'row')};
  cursor: pointer !important;
  outline: 0;
  border-radius: 100px;
  gap: 8px;
  position: relative;
  background: ${(props) => props.styles.backgroundColor};
  color: ${(props) => props.textColor ?? props.styles.color};
  border: ${(props) => props.styles.border};
  border-image: ${(props) => props.styles.borderImage};
  padding: ${(props) => props.styles.padding};
  height: ${(props) => props.styles.height};
  ${space}
  ${layout}

  .spinner {
    border-color: ${(props) => props.textColor ?? props.styles.color};
  }

  > span {
    direction: ltr;
    unicode-bidi: bidi-override;
    font-size: ${(props) => props.styles.fontSize};
    font-weight: ${(props) => props.styles.fontWeight};
    line-height: ${(props) => props.styles.lineHeight};
    font-family: ${(props) => props.styles.fontFamily};
  }

  * {
    cursor: pointer !important;
  }

  &:hover {
    background: ${(props) => props.styles.backgroundColorHover};
    color: ${(props) => props.styles.colorHover};
    border: ${(props) => props.styles.borderHover};

    svg path {
      fill: ${(props) => props.styles.iconColorHover};
    }

    * {
      color: ${(props) => props.styles.colorHover};
    }
  }

  :disabled {
    cursor: not-allowed !important;
    background: ${(props) => props.styles.backgroundColorDisabled} !important;
    border: ${(props) => props.styles.borderDisabled} !important;

    > span {
      color: ${(props) => props.styles.colorDisabled} !important;
    }

    svg path {
      fill: ${(props) => props.styles.colorDisabled};
    }

    * {
      color: ${(props) => props.styles.colorDisabled};
      cursor: not-allowed !important;
    }
  }
`;

export const RightIconWrapper = styled.div<{ iconSize: IconSize; hasMargin: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    height: ${(props) => props.iconSize};
    width: ${(props) => props.iconSize};
    transform: ${(props) => (props.theme.rtl ? 'scaleX(-1)' : 'unset')};
  }
`;

export const LeftIconWrapper = styled.div<{ iconSize: IconSize; hasMargin: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    height: ${(props) => props.iconSize};
    width: ${(props) => props.iconSize};
    transform: ${(props) => (props.theme.rtl ? 'scaleX(-1)' : 'unset')};
  }
`;

export const TextWrapper = styled.span`
  white-space: nowrap;
`;
